import React, { useState } from 'react';
import { NotificationListItem } from 'services/notificationService';
import classes from './notificationItem.module.scss';

interface NotificationItemProps {
  notification: NotificationListItem;
  onMarkAsRead: (id: number) => void;
  onClick?: (notification: NotificationListItem) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onClick,
}) => {
  const [showFullMessage, setShowFullMessage] = useState(false);
  
  // Check if message is truncated (more than 2 lines)
  const messageRef = React.useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  
  React.useEffect(() => {
    if (messageRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(messageRef.current).lineHeight || '20');
      const maxHeight = lineHeight * 2; // 2 lines
      setIsTruncated(messageRef.current.scrollHeight > maxHeight);
    }
  }, [notification.message]);
  /**
   * Format timestamp to relative time (e.g., "2 hours ago")
   */
  const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      // For older notifications, show date
      return time.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: time.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger notification click if clicking on "View Full" button
    if ((e.target as HTMLElement).closest('.viewFullButton')) {
      return;
    }
    if (onClick) {
      onClick(notification);
    }
    // Mark as read when clicked (if not already read)
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
  };

  const handleViewFullClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFullMessage(!showFullMessage);
  };

  return (
    <div
      className={`${classes.notificationItem} ${notification.is_read ? classes.read : classes.unread}`}
      onClick={handleClick}
    >
      <div className={classes.notificationContent}>
        <div className={classes.notificationHeader}>
          <h4 className={classes.notificationTitle}>{notification.title}</h4>
          {!notification.is_read && <div className={classes.unreadDot} />}
        </div>
        <div style={{ position: 'relative' }}>
          <p 
            ref={messageRef}
            className={`${classes.notificationMessage} ${showFullMessage ? classes.expanded : ''}`}
            title={!showFullMessage && isTruncated ? notification.message : undefined}
          >
            {notification.message}
          </p>
          {isTruncated && (
            <button
              className={`${classes.viewFullButton} viewFullButton`}
              onClick={handleViewFullClick}
              type="button"
            >
              {showFullMessage ? 'Show Less' : 'View Full'}
            </button>
          )}
        </div>
        <span className={classes.notificationTime}>
          {formatRelativeTime(notification.created_at)}
        </span>
      </div>
    </div>
  );
};

export default NotificationItem;
